// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './CollectComplete.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { useTranslation } from 'react-i18next';
import { CompressionDeliveryContent } from 'components/molecules/CompressionDeliveryContent';
import { DATE_FORMAT } from 'util/ConstantValues';
import { toast } from 'react-toastify';
import { BottomField } from 'components/molecules/BottomField';
import { EmissionEditModal } from '../EmissionEditModal';
import { Collect, CollectListResponse, UncollectEmission } from 'util/Types';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import { ConfirmModal } from '../ConfirmModal';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { handleError } from 'util/errorHandler';
import CustomPagination from 'components/atoms/CustomPagination';
import { CheckBox } from 'components/atoms/CheckBox';
import CollectDeliveriesDataModal from 'components/molecules/CollectDeliveriesDataModal';
import { getCollectDeliveries } from 'apis/collects';

const MAX_SELECTED_COLLECTS = 200;

export function CollectComplete() {
  const { t } = useTranslation();
  const [collects, setCollects] = useState<Collect[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });
  const [selectedCollects, setSelectedCollects] = useState<Collect[]>([]);
  const [emissionDetailCollectId, setEmissionDetailCollectId] =
    useState<string>();

  const [substituteRegists, setSubstituteRegists] = useState<
    UncollectEmission[]
  >([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingCollect, setDeletingCollect] = useState<Collect | undefined>();
  const [deletingCollectEmissionIds, setDeletingCollectEmissionIds] = useState<
    string[]
  >([]);

  const pageCount = Math.ceil(pageMeta.total / 200);

  const submit = async () => {
    try {
      await createAxios().post('/deliveries', {
        deliveryIds: selectedCollects.map(({ id }) => id),
      });
      toast.success(t('messages.M_088'));

      const newPageCount = Math.ceil(
        pageMeta.total - selectedCollects.length / 200
      );

      callGetCollectsApi({
        page: Math.min(pageMeta.page, newPageCount),
        resetSelectedCollectsOnSuccess: true,
      });
      callGetSubstituteRegistApi();
      setSelectedCollects([]);
    } catch (e) {
      handleError(e);
    }
  };

  const callGetCollectsApi = async (params?: {
    page?: number;
    resetSelectedCollectsOnSuccess?: boolean;
  }) => {
    const { page = pageMeta.page, resetSelectedCollectsOnSuccess } =
      params || {};

    try {
      const response = await createAxios({
        prefix: 'v2',
      }).get<CollectListResponse>('/collects', {
        params: {
          page,
          take: 200,
        },
      });

      setCollects(response.data.deliveries);

      setPageMeta({
        page: response.data.page,
        total: response.data.total,
      });

      if (resetSelectedCollectsOnSuccess) {
        setSelectedCollects([]);
      } else {
        const newSelectedCollects = response.data.deliveries.filter((collect) =>
          selectedCollects.some((c) => c.id === collect.id)
        );

        if (newSelectedCollects.length) {
          setSelectedCollects((prev) =>
            prev
              .filter((c) => newSelectedCollects.every((nc) => nc.id !== c.id))
              .concat(newSelectedCollects)
          );
        }
      }
    } catch (e) {
      handleError(e);
    }
  };

  const getDeletingCollectEmissionIds = async () => {
    if (!deletingCollect) {
      return;
    }

    getCollectDeliveries({
      deliveryIds: [deletingCollect.id],
      cursor: null,
      successCallback: (data) => {
        setDeletingCollectEmissionIds(
          data.map((item) => item.processingObjectId).filter(Boolean)
        );
      },
      failedCallback: () => {
        setDeletingCollectEmissionIds([]);
        setDeletingCollect(undefined);
      },
    });
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    callGetCollectsApi({
      page: selectedItem.selected + 1,
    });
  };

  const callGetSubstituteRegistApi = async () => {
    try {
      const response = await createAxios().get('collects/substitute-regists');
      setSubstituteRegists(response.data);
    } catch (e) {
      handleError(e);
    }
  };

  const callDeleteSubstituteRegistApi = async (id: string) => {
    try {
      await createAxios().delete(`collects/substitute-regists/${id}`);
      toast.success(t('messages.M_012'));

      setDeletingCollect(undefined);
      setDeletingCollectEmissionIds([]);
      await callGetCollectsApi();
      setSelectedCollects((prev) =>
        prev.filter((c) => c.id !== deletingCollect?.id)
      );
      callGetSubstituteRegistApi();
    } catch (_) {
      toast.error(t('messages.M_013'));
    }
  };

  const handleOnDeleteSubstituteRegistData = async () => {
    if (!deletingCollect || !deletingCollectEmissionIds.length) {
      return;
    }

    await callDeleteSubstituteRegistApi(deletingCollectEmissionIds[0]);
  };

  useEffect(() => {
    callGetCollectsApi();
    callGetSubstituteRegistApi();
  }, []);

  useEffect(() => {
    getDeletingCollectEmissionIds();
  }, [deletingCollect]);

  const totalWeight = useMemo(
    () => selectedCollects.reduce((total, c) => total + Number(c.weight), 0),
    [selectedCollects]
  );

  const handleSelectCollect = (collect: Collect) => {
    if (
      !selectedCollects.some((c) => c.id === collect.id) &&
      selectedCollects.length >= MAX_SELECTED_COLLECTS
    ) {
      toast.error(
        t('messages.M_collects_receive_limit_exceeded', {
          max: MAX_SELECTED_COLLECTS,
        })
      );
      return;
    }

    setSelectedCollects((prev) => {
      if (prev.some((c) => c.id === collect.id)) {
        return prev.filter((c) => c.id !== collect.id);
      }

      return [...prev, collect];
    });
  };

  const handleSelectAll = () => {
    if (!collects.length) {
      return;
    }

    if (collects.every(({ id }) => selectedCollects.some((c) => c.id === id))) {
      setSelectedCollects((prev) =>
        prev.filter((c) => !collects.find((collect) => collect.id === c.id))
      );
    } else {
      const newSelectedCollects = collects.filter(
        (collect) => !selectedCollects.some((c) => c.id === collect.id)
      );

      if (
        newSelectedCollects.length + selectedCollects.length >
        MAX_SELECTED_COLLECTS
      ) {
        toast.error(
          t('messages.M_collects_receive_limit_exceeded', {
            max: MAX_SELECTED_COLLECTS,
          })
        );
        return;
      }

      setSelectedCollects((prev) => [...prev, ...newSelectedCollects]);
    }
  };

  const renderListCollects = () => {
    if (collects.length === 0) {
      return <div className={styles.noData}>{t('messages.M_008')}</div>;
    }

    return collects.map((collect) => {
      return (
        <div className={styles.collectCompleteCard} key={collect.id}>
          <CheckBox
            id={collect.id}
            checked={selectedCollects.some((c) => c.id === collect.id)}
            onChange={() => handleSelectCollect(collect)}
          />

          <div className={styles.collectCompleteContentRow}>
            <div className={styles.collectCompleteContentCol}>
              <div className={styles.collectCompleteContent}>
                <div className={styles.collectCompleteLabel}>
                  {t('common_history_field.date_time')}
                </div>
                <CompressionDeliveryContent
                  contents={getFormatDateWithCurrentTimezone(
                    collect.collectedDatetime,
                    DATE_FORMAT.slaYMDHm
                  )}
                />
              </div>
              <div className={styles.collectCompleteContent}>
                <div className={styles.collectCompleteLabel}>
                  {t('common_history_field.responsible_person')}
                </div>
                <CompressionDeliveryContent
                  contents={
                    collect.deliveryUserId +
                    '(' +
                    collect.deliveryUserName +
                    ')'
                  }
                />
              </div>
              <div className={styles.collectCompleteContent}>
                <div className={styles.collectCompleteLabel}>
                  {t('common_history_field.place')}
                </div>
                <CompressionDeliveryContent
                  contents={collect.collectedAreaName}
                />
              </div>
              <div className={styles.collectCompleteContent}>
                <div className={styles.collectCompleteLabel}>
                  {t('common_history_field.item')}
                </div>
                <CompressionDeliveryContent contents={collect.description} />
              </div>
              <div className={styles.collectCompleteContent}>
                <div className={styles.collectCompleteLabel}>
                  {t('common_history_field.weight')}
                </div>
                <CompressionDeliveryContent
                  contents={
                    normalizeNumber({
                      value: collect.weight,
                    }) + 'kg'
                  }
                />
              </div>
            </div>
            <div className={styles.collectCompleteActionCol}>
              <button
                className={styles.viewButton}
                onClick={() => setEmissionDetailCollectId(collect.id)}
              >
                {t('common.button.view_details')}
              </button>

              {collect.substituteRegist && (
                <div
                  className={styles.actionDelete}
                  onClick={() => {
                    setDeletingCollect(collect);
                  }}
                >
                  <IconDelete stroke="white" />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.collectComplete}>
      <div className={styles.collectActions}>
        <div className={styles.selectAll}>
          <CheckBox
            id="select-all"
            checked={
              collects.length > 0 &&
              collects.every(({ id }) =>
                selectedCollects.some((c) => c.id === id)
              )
            }
            onChange={handleSelectAll}
            disabled={collects.length === 0}
          />

          <span>{t('common.select_all')}</span>
        </div>

        <div className={styles.selectedInfo}>
          <span>
            {t('common_history_field.total_selected_item', {
              totalCount: selectedCollects.length,
            })}
          </span>
          <span>
            {t('common_history_field.total_selected_weight', {
              totalWeight: normalizeNumber({
                value: totalWeight,
              }),
            })}
          </span>
        </div>
      </div>

      <div
        className={mergeClasses(styles.scrollContents, {
          [styles.scrollContentsWithPagination]: pageCount > 1,
        })}
      >
        {renderListCollects()}
      </div>

      <BottomField
        className={mergeClasses(styles.bottomField, {
          [styles.bottomWithPagination]: pageCount > 1,
        })}
      >
        <div className={styles.bottomBtn}>
          <div className={styles.bottomBtnActions}>
            {substituteRegists.length > 0 && (
              <button
                className={styles.btnEditing}
                type="button"
                disabled={false}
                onClick={() => {
                  setIsEditModalOpen(true);
                }}
              >
                {t('collects.edit_substitute_regist_data')}
              </button>
            )}

            <button
              className={styles.btnRequest}
              type="button"
              disabled={!selectedCollects.length}
              onClick={submit}
            >
              {t('collects.request_receipt')}
            </button>
          </div>

          {pageCount > 1 && (
            <CustomPagination
              pageCount={pageCount}
              onPageChange={handlePageClick}
              forcePage={Math.max(pageMeta.page - 1, 0)}
            />
          )}
        </div>
      </BottomField>
      {isEditModalOpen && (
        <EmissionEditModal
          onClose={() => {
            setIsEditModalOpen(false);
          }}
          afterSubmit={() => {
            callGetCollectsApi();
            callGetSubstituteRegistApi();
          }}
          emissionData={substituteRegists}
        />
      )}
      <ConfirmModal
        title={t('common.alert.delete')}
        submitLabel={t('common.button.delete') || '削除'}
        alert={t('collects.substitute_regist_delete_alert') || ''}
        isOpen={deletingCollect != null}
        closeEditModal={() => {
          setDeletingCollect(undefined);
          setDeletingCollectEmissionIds([]);
        }}
        onClick={handleOnDeleteSubstituteRegistData}
        confirmContents={[
          {
            label: t('common_history_field.item'),
            contents: deletingCollect?.description || '',
          },
          {
            label: t('common_history_field.weight'),
            contents: (deletingCollect?.weight || 0) + 'kg',
          },
          {
            label: 'ID',
            contents: (deletingCollectEmissionIds || []).join('/'),
          },
        ]}
      />

      <CollectDeliveriesDataModal
        onClose={() => setEmissionDetailCollectId(undefined)}
        collectId={emissionDetailCollectId}
      />
    </div>
  );
}
